// Story 10.3 — Replay player. La lógica vive en `replay-reducer.ts`; este
// componente solo wirea el setTimeout loop + atajos de teclado.

import { Pause, Play, SkipForward, X } from 'lucide-react';
import { useEffect, useReducer } from 'react';
import { Button } from '@/components/ui/button';
import type { ConversationMessage, FlowEvent } from '@/lib/conversations-types';
import { initReplayState, REPLAY_DEFAULTS, replayReducer, takeVisible } from '@/lib/replay-reducer';
import { cn } from '@/lib/utils';
import { MessageBubble } from './MessageBubble';

interface Props {
  messages: ConversationMessage[];
  flowEvents: FlowEvent[];
  onExit: () => void;
}

export function ReplayPlayer({ messages, flowEvents, onExit }: Props): React.ReactElement {
  const [state, dispatch] = useReducer(replayReducer, messages.length, (n) => initReplayState(n));

  useEffect(() => {
    dispatch({ type: 'set-total', total: messages.length });
  }, [messages.length]);

  // setTimeout loop (cleanup en pause/unmount). `currentIndex` está en deps
  // adrede: cada tick avanza el índice y re-arma el timer del siguiente.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-trigger by design
  useEffect(() => {
    if (!state.playing) return;
    const t = window.setTimeout(() => dispatch({ type: 'tick' }), state.speedMs);
    return () => window.clearTimeout(t);
  }, [state.playing, state.speedMs, state.currentIndex]);

  // Atajos de teclado: Esc=salir, Space=play/pause, →=step.
  // P-266: si el foco está en un input/textarea/contenteditable (e.g. la
  // search box de filtros sigue mounted en otro panel), NO interceptamos
  // Space ni ArrowRight — el user está escribiendo. Esc sí escapa siempre,
  // es convención universal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target != null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === 'Escape') {
        e.preventDefault();
        onExit();
      } else if (!inEditable && e.key === ' ') {
        e.preventDefault();
        dispatch({ type: state.playing ? 'pause' : 'play' });
      } else if (!inEditable && e.key === 'ArrowRight') {
        e.preventDefault();
        dispatch({ type: 'step' });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.playing, onExit]);

  const visible = takeVisible(messages, state);
  // Encuentra el flow event activo: el último cuyo timestamp <= timestamp del último mensaje visible.
  // P-273: backwards-scan sin reverse() para no allocar nuevo array por tick.
  const lastMsg = visible[visible.length - 1];
  let activeFlow: FlowEvent | null = null;
  if (lastMsg && flowEvents.length > 0) {
    for (let i = flowEvents.length - 1; i >= 0; i--) {
      if (flowEvents[i].timestamp <= lastMsg.timestamp) {
        activeFlow = flowEvents[i];
        break;
      }
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={() => dispatch({ type: state.playing ? 'pause' : 'play' })}
            aria-label={state.playing ? 'Pausar' : 'Reproducir'}
          >
            {state.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {state.playing ? 'Pausar' : 'Reproducir'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => dispatch({ type: 'step' })}
            aria-label="Avanzar un paso"
          >
            <SkipForward className="h-4 w-4" /> Step
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => dispatch({ type: 'restart' })}
            aria-label="Reiniciar"
          >
            Reiniciar
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Velocidad
            <input
              type="range"
              min={REPLAY_DEFAULTS.minSpeedMs}
              max={REPLAY_DEFAULTS.maxSpeedMs}
              step={100}
              value={state.speedMs}
              onChange={(e) => dispatch({ type: 'set-speed', speedMs: Number(e.target.value) })}
              aria-label="Velocidad del replay (ms entre mensajes)"
            />
            <span className="font-mono text-xs">{state.speedMs}ms</span>
          </label>
          <span className="text-xs text-muted-foreground">
            {state.currentIndex} / {state.total}
          </span>
          <Button size="icon" variant="ghost" onClick={onExit} aria-label="Salir del replay">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {activeFlow && (
        <div
          className={cn(
            'border-b border-border px-4 py-2 text-xs',
            activeFlow.eventName === 'flow:error'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted/50 text-muted-foreground',
          )}
          aria-live="polite"
        >
          <strong>{activeFlow.eventName}</strong>
          {activeFlow.flowId && <> · flow={activeFlow.flowId}</>}
          {activeFlow.stepId && <> · step={activeFlow.stepId}</>}
          {activeFlow.stepIndex != null && <> (#{activeFlow.stepIndex})</>}
          {activeFlow.errorMessage && <> — {activeFlow.errorMessage}</>}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {visible.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>
    </div>
  );
}
